/**
 * Single source of truth for the Google AdSense client ID.
 *
 * The codebase grew two separate AdSense integrations at different times -
 * AdSenseAd.tsx / AdSenseScript.tsx read `VITE_ADSENSE_PUBLISHER_ID`, while
 * GoogleAd.tsx reads `VITE_GOOGLE_AD_CLIENT`. Both expect the same value (the
 * `ca-pub-XXXXXXXXXXXXXXXX` client ID used in the `data-ad-client` attribute
 * and the adsbygoogle.js script URL), so setting only one of the two
 * Cloudflare Pages env vars used to silently leave half the ad slots blank.
 * This helper reads whichever is set so a single env var is enough.
 *
 * The AdSense *publisher* ID (the `pub-XXXXXXXXXXXXXXXX` form used in
 * client/public/ads.txt) is confirmed in the account's Account information
 * screen; the `ca-` prefixed client ID used here is the same number with
 * `ca-` prepended.
 */
export function getAdSenseClientId(): string | undefined {
  const fromAdSenseVar = import.meta.env.VITE_ADSENSE_PUBLISHER_ID as string | undefined;
  const fromGoogleAdVar = import.meta.env.VITE_GOOGLE_AD_CLIENT as string | undefined;
  for (const configured of [fromGoogleAdVar, fromAdSenseVar]) {
    const raw = configured?.trim();
    if (!raw) continue;
    // Accept either "pub-XXXX" or "ca-pub-XXXX" - always return the ca- form
    // AdSense's script tag and data-ad-client attribute expect.
    const clientId = raw.startsWith("ca-") ? raw : `ca-${raw}`;
    if (/^ca-pub-\d+$/.test(clientId)) return clientId;
  }
  return undefined;
}

/** AdSense ad-unit slots are numeric IDs issued by the AdSense console. */
export function isAdSenseSlotId(value: string | null | undefined): boolean {
  return /^\d+$/.test(`${value || ""}`.trim());
}

/** Resolve either a numeric slot or a semantic placement via Vite env. */
export function getAdSenseSlotId(
  slotOrPlacement: string | null | undefined,
): string | undefined {
  const value = `${slotOrPlacement || ""}`.trim();
  if (isAdSenseSlotId(value)) return value;
  if (!value) return undefined;
  const envKey = `VITE_ADSENSE_SLOT_${value.replace(/[^a-z0-9]+/gi, "_").toUpperCase()}`;
  const configured = (import.meta.env as Record<string, unknown>)[envKey];
  return isAdSenseSlotId(String(configured || ""))
    ? String(configured).trim()
    : undefined;
}
